/**
 * The world's clock: real time by default, or any instant and any speed.
 *
 *   ?time=2026-12-21T15:30Z   start at an instant (UTC, or with an offset)
 *   ?timescale=600            ten minutes of sky per real second
 *
 * Everything time-dependent reads this one clock — the sun, the moon, the
 * season the tiles are built for, which windows are lit.
 *
 * It is anchored to the device's own clock rather than counted up frame by
 * frame: a page in a background tab, or a phone asleep, draws no frames, and a
 * clock added up from frame times stops with them. This one reads real time
 * whenever it is asked, so it is the device's time to the second — only ever
 * shifted by the hours stepped with `[` `]`, and sped up by a time-lapse.
 */

export class SkyClock {
  /** The sky's instant at `anchorReal` on the device's clock, ms. */
  private anchorSky: number;
  private anchorReal: number;
  private scale: number;

  constructor(start: Date = new Date(), timescale = 1, private readonly realNow: () => number = Date.now) {
    this.anchorSky = start.getTime();
    this.anchorReal = realNow();
    this.scale = timescale;
  }

  get now(): Date {
    return new Date(this.anchorSky + (this.realNow() - this.anchorReal) * this.scale);
  }

  /** Sky seconds per real second. Changing it keeps the sky where it is now. */
  get timescale(): number {
    return this.scale;
  }

  set timescale(value: number) {
    this.anchorSky = this.now.getTime();
    this.anchorReal = this.realNow();
    this.scale = value;
  }

  shiftHours(hours: number): void {
    this.anchorSky += hours * 3_600_000;
  }
}

/** The time on the device's own clock and in its own time zone, as "14:05". */
export function localClock(date: Date): string {
  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
}

/** The date and time zone the device is in, as "2026-09-30 CEST". */
export function localDate(date: Date): string {
  const d = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  const zone = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' }).formatToParts(date).find(p => p.type === 'timeZoneName')?.value ?? '';
  return `${d} ${zone}`.trim();
}

/** Local mean solar time at a longitude, hours 0..24. */
export function solarHour(date: Date, lon: number): number {
  const utc = date.getUTCHours() + date.getUTCMinutes() / 60 + date.getUTCSeconds() / 3600;
  return (((utc + lon / 15) % 24) + 24) % 24;
}

/**
 * Share of windows with a light on, by local hour.
 *
 * Empty offices and bright days early; a peak in the evening when everyone is
 * home; lights going out through the night until a few insomniacs and early
 * risers remain. Each window has its own threshold, so as this falls the town
 * goes dark one window at a time rather than all at once.
 */
export function windowsLit(hour: number): number {
  const curve: Array<[number, number]> = [
    [0, 0.2], [2, 0.09], [4.5, 0.07], [6.5, 0.32], [8, 0.14], [15.5, 0.12],
    [18, 0.6], [21, 0.58], [23, 0.34], [24, 0.2],
  ];
  for (let i = 1; i < curve.length; i++) {
    if (hour <= curve[i][0]) {
      const [h0, v0] = curve[i - 1];
      const [h1, v1] = curve[i];
      return v0 + ((v1 - v0) * (hour - h0)) / (h1 - h0);
    }
  }
  return curve[curve.length - 1][1];
}

/** Parse the clock from a query string. Malformed values fall back to now. */
export function clockFromSearch(search: string): SkyClock {
  const params = new URLSearchParams(search);
  const raw = params.get('time');
  const parsed = raw === null ? NaN : Date.parse(raw);
  const scale = Number(params.get('timescale') ?? 1);
  return new SkyClock(Number.isFinite(parsed) ? new Date(parsed) : new Date(), Number.isFinite(scale) ? scale : 1);
}
